"use client";

import type { DefaultError, UseQueryOptions } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useSession } from "../../session/hooks/useSession";
import type { paths } from "../generated/schema";
import { createOpenApiClient } from "../openApiClient";
import { useHeadersValues } from "./useHeadersValues";

// Type helpers for extracting paths that have a GET method
type PathsWithGet = {
  [P in keyof paths]: paths[P] extends { get: unknown } ? P : never;
}[keyof paths];

// Extract parameters for a GET request
type GetParams<P extends PathsWithGet> = paths[P]["get"] extends {
  parameters: infer Params;
}
  ? Params
  : undefined;

// Extract successful response type for a GET request
type GetResponse<P extends PathsWithGet> = paths[P]["get"] extends {
  responses: {
    200: { content: { "application/json": infer R } };
  };
}
  ? R
  : unknown;

export type UseOpenApiQueryOptions<
  P extends PathsWithGet,
  TData = GetResponse<P>,
> = Omit<
  Partial<UseQueryOptions<GetResponse<P>, DefaultError, TData>>,
  "queryFn"
> & {
  path: P;
  params?: GetParams<P>;
  enabledWithoutSessionToken?: boolean;
};

export const useOpenApiQuery = <
  P extends PathsWithGet,
  TData = GetResponse<P>,
>({
  path,
  params,
  enabled = true,
  enabledWithoutSessionToken = false,
  queryKey,
  ...options
}: UseOpenApiQueryOptions<P, TData>) => {
  const headersValues = useHeadersValues();
  const { token, setToken } = useSession();

  const client = useMemo(
    () =>
      createOpenApiClient({
        headersValues: headersValues.current,
        onTokenRefresh: setToken,
      }),
    [headersValues.current.token, headersValues.current.locale, setToken],
  );

  return useQuery({
    enabled:
      (enabledWithoutSessionToken ? true : typeof token === "string") &&
      enabled,
    queryKey: queryKey ?? [path, params, headersValues.current.locale],
    queryFn: async () => {
      const { data, error } = await client.GET(path, params as any);
      if (error) throw error;
      return data as GetResponse<P>;
    },
    ...options,
  });
};
